/**
 * FASE 20.8 — CACHED OPERATIONAL SNAPSHOTS
 *
 * In-memory LRU-style cache for Kevia data tool results.
 * Keyed by `${toolId}:${scopeKey}`. TTL from tool schema.
 * Never persisted — session only.
 */

const MAX_ENTRIES = 30;

/** @type {Map<string, { data: any, expiresAt: number, toolId: string }>} */
const _cache = new Map();

function _evict() {
  if (_cache.size < MAX_ENTRIES) return;
  // Remove the oldest entry
  const oldestKey = _cache.keys().next().value;
  if (oldestKey) _cache.delete(oldestKey);
}

/**
 * Build a deterministic cache key from tool ID + scope filters.
 * @param {string} toolId
 * @param {Object} filters
 * @returns {string}
 */
export function buildCacheKey(toolId, filters = {}) {
  const parts = [toolId];
  const sorted = Object.entries(filters)
    .filter(([, v]) => v != null)
    .sort(([a], [b]) => a.localeCompare(b));
  for (const [k, v] of sorted) parts.push(`${k}=${v}`);
  return parts.join(":");
}

/**
 * @param {string} key
 * @returns {{ data: any, toolId: string } | null}
 */
export function cacheGet(key) {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    _cache.delete(key);
    return null;
  }
  // Move to end (LRU: recently accessed = most recent)
  _cache.delete(key);
  _cache.set(key, entry);
  return { data: entry.data, toolId: entry.toolId };
}

/**
 * @param {string} key
 * @param {any} data
 * @param {number} ttlMs
 * @param {string} toolId
 */
export function cacheSet(key, data, ttlMs, toolId) {
  _evict();
  _cache.set(key, { data, expiresAt: Date.now() + ttlMs, toolId });
}

export function cacheInvalidate(toolId) {
  for (const [key, entry] of _cache.entries()) {
    if (entry.toolId === toolId) _cache.delete(key);
  }
}

export function cacheInvalidateAll() {
  _cache.clear();
}

/** Dev introspection — returns snapshot of current cache state */
export function getCacheSnapshot() {
  const now = Date.now();
  return Array.from(_cache.entries()).map(([key, e]) => ({
    key,
    toolId:    e.toolId,
    ttlLeft:   Math.max(0, e.expiresAt - now),
    rows:      Array.isArray(e.data) ? e.data.length : (e.data != null ? 1 : 0),
  }));
}
